import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  StepIcon,
  Grid,
  Divider,
  Paper
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Build as BuildIcon,
  Rocket as RocketIcon,
  PlayArrow as PlayArrowIcon,
  Update as UpdateIcon,
  ModelTraining as ModelTrainingIcon,
  CloudDone as CloudDoneIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';

// VLM Sensor States
const VLM_STATES = {
  CONFIGURED: 'configured',
  INTRAINING: 'intraining', 
  DEPLOYED: 'deployed',
  ERROR: 'error'
};

const STATE_CONFIG = {
  [VLM_STATES.CONFIGURED]: {
    label: 'Configured',
    color: 'info',
    icon: <ScheduleIcon />,
    description: 'Sensor configured and ready for training'
  },
  [VLM_STATES.INTRAINING]: {
    label: 'In Training',
    color: 'warning',
    icon: <BuildIcon />,
    description: 'Model is being trained with your prompt'
  },
  [VLM_STATES.DEPLOYED]: {
    label: 'Deployed',
    color: 'success',
    icon: <RocketIcon />,
    description: 'Model deployed and actively monitoring'
  },
  [VLM_STATES.ERROR]: {
    label: 'Error',
    color: 'error',
    icon: <RefreshIcon />,
    description: 'Error in training or deployment'
  }
};

const VLMSensorStateDisplay = ({ 
  sensorData, 
  onReconfigure, 
  onViewPrompt,
  onRefresh 
}) => {
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [showProgressDialog, setShowProgressDialog] = useState(false);

  // Default sensor data structure
  const defaultSensorData = {
    state: VLM_STATES.CONFIGURED,
    promptConfigs: [{
      prompt: '',
      title: 'Custom Prompt',
      category: 'Custom',
      isCustom: true
    }],
    trainingProgress: {
      currentStep: 0,
      totalSteps: 5,
      estimatedCompletion: null,
      startedAt: null,
      completedAt: null
    },
    deploymentInfo: {
      modelVersion: null,
      deployedAt: null,
      eventsDetected: 0,
      lastEventAt: null
    },
    error: null
  };

  // Safely merge sensor data with defaults, handling nested objects
  const sensor = {
    ...defaultSensorData,
    ...sensorData,
    promptConfigs: sensorData?.promptConfigs || 
                   (sensorData?.promptConfig ? [sensorData.promptConfig] : defaultSensorData.promptConfigs),
    trainingProgress: {
      ...defaultSensorData.trainingProgress,
      ...(sensorData?.trainingProgress || {})
    },
    deploymentInfo: {
      ...defaultSensorData.deploymentInfo,
      ...(sensorData?.deploymentInfo || {})
    }
  };
  const stateConfig = STATE_CONFIG[sensor.state];

  const getProgressPercentage = () => {
    if (sensor.state === VLM_STATES.DEPLOYED) return 100;
    if (sensor.state === VLM_STATES.INTRAINING) {
      return (sensor.trainingProgress.currentStep / sensor.trainingProgress.totalSteps) * 100;
    }
    if (sensor.state === VLM_STATES.CONFIGURED) return 0;
    return 0;
  };

  const getEstimatedCompletion = () => {
    if (sensor.trainingProgress.estimatedCompletion) {
      const date = new Date(sensor.trainingProgress.estimatedCompletion);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    }
    return 'Calculating...';
  };

  const trainingSteps = [
    { label: 'Data Collection', description: 'Gathering historical video data' },
    { label: 'VSS Analysis', description: 'NVIDIA VSS analyzing footage' },
    { label: 'Event Detection', description: 'Identifying relevant events' },
    { label: 'Synthetic Data Generation', description: 'NVIDIA Cosmos creating training data' },
    { label: 'Model Training', description: 'Training custom VLM model' }
  ];

  // Helper function to get active step index
  const getActiveStep = () => {
    switch (sensor.state) {
      case VLM_STATES.CONFIGURED: return 0;
      case VLM_STATES.INTRAINING: return 1;
      case VLM_STATES.DEPLOYED: return 2;
      default: return 0;
    }
  };

  // Check if this is retraining (has previous deployment)
  const isRetraining = sensor.configHistory && sensor.configHistory.length > 0 && 
                      sensor.deploymentInfo && sensor.deploymentInfo.deployedAt;

  // Get training ETA
  const getTrainingETA = () => {
    if (sensor.trainingProgress?.estimatedCompletion) {
      const eta = new Date(sensor.trainingProgress.estimatedCompletion);
      const now = new Date();
      const diffHours = Math.ceil((eta - now) / (1000 * 60 * 60));
      
      if (diffHours > 24) {
        const days = Math.ceil(diffHours / 24);
        return `${days} day${days > 1 ? 's' : ''}`;
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
      } else {
        return 'Less than 1 hour';
      }
    }
    return 'Calculating...';
  };

  // Custom Step Icon Component with proper colors
  const CustomStepIcon = ({ active, completed, icon, stepIndex }) => {
    let bgcolor, color;
    
    if (completed) {
      bgcolor = 'success.main';
      color = 'white';
    } else if (active) {
      // Different colors for each active step
      switch (stepIndex) {
        case 0: // Configured
          bgcolor = 'primary.main';
          color = 'white';
          break;
        case 1: // Training
          bgcolor = 'warning.main';
          color = 'white';
          break;
        case 2: // Deployed
          bgcolor = 'success.main';
          color = 'white';
          break;
        default:
          bgcolor = 'primary.main';
          color = 'white';
      }
    } else {
      bgcolor = 'grey.300';
      color = 'grey.600';
    }

    return (
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor,
          color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: active ? 3 : 0,
          borderColor: active ? `${bgcolor}40` : 'transparent',
          boxShadow: active ? 3 : (completed ? 1 : 0),
          transition: 'all 0.3s ease',
          position: 'relative',
          '&::before': active ? {
            content: '""',
            position: 'absolute',
            top: -4,
            left: -4,
            right: -4,
            bottom: -4,
            borderRadius: '50%',
            border: `2px solid ${bgcolor}30`,
            animation: 'pulse 2s infinite'
          } : {},
          '@keyframes pulse': {
            '0%': {
              transform: 'scale(1)',
              opacity: 1
            },
            '50%': {
              transform: 'scale(1.1)',
              opacity: 0.7
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1
            }
          }
        }}
      >
        {React.cloneElement(icon, { fontSize: 'small' })}
      </Box>
    );
  };

  const steps = [
    {
      label: 'Configured',
      description: `${sensor.promptConfigs?.length || 0} prompt${sensor.promptConfigs?.length !== 1 ? 's' : ''} configured`,
      icon: <CheckCircleIcon />,
      completed: sensor.state !== VLM_STATES.CONFIGURED,
      color: 'primary'
    },
    {
      label: isRetraining ? 'Retraining' : 'Training',
      description: sensor.state === VLM_STATES.INTRAINING 
        ? `Step ${sensor.trainingProgress?.currentStep || 0}/${sensor.trainingProgress?.totalSteps || 5} • ETA: ${getTrainingETA()}`
        : isRetraining ? 'Retraining with new configuration' : 'Training custom VLM model',
      icon: isRetraining ? <UpdateIcon /> : <ModelTrainingIcon />,
      completed: sensor.state === VLM_STATES.DEPLOYED,
      color: 'warning'
    },
    {
      label: 'Deployed',
      description: sensor.state === VLM_STATES.DEPLOYED 
        ? `Active since ${sensor.deploymentInfo?.deployedAt ? new Date(sensor.deploymentInfo.deployedAt).toLocaleDateString() : 'Unknown'}`
        : 'Ready for deployment',
      icon: <CloudDoneIcon />,
      completed: sensor.state === VLM_STATES.DEPLOYED,
      color: 'success'
    }
  ];

  return (
    <Box>
      <Card>
        <CardContent>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Dynamic VLM Sensor
              </Typography>
              {isRetraining && (
                <Chip 
                  icon={<UpdateIcon />}
                  label="Retraining Mode"
                  color="warning"
                  size="small"
                  sx={{ mb: 1 }}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="View Prompt Configuration">
                <IconButton onClick={() => setShowPromptDialog(true)} size="small">
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
              
              {sensor.state === VLM_STATES.INTRAINING && (
                <Tooltip title="View Training Progress">
                  <IconButton onClick={() => setShowProgressDialog(true)} size="small">
                    <BuildIcon />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Reconfigure Prompts">
                <IconButton onClick={onReconfigure} size="small">
                  <SettingsIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Refresh Status">
                <IconButton onClick={onRefresh} size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* VLM Training Stepper */}
          <Stepper 
            activeStep={getActiveStep()} 
            orientation="horizontal" 
            sx={{ 
              mb: 3,
              '& .MuiStepConnector-line': {
                borderTopWidth: 3,
                borderRadius: 1
              },
              '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                borderColor: 'success.main'
              },
              '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
                borderColor: 'warning.main'
              }
            }}
          >
            {steps.map((step, index) => {
              const isActive = index === getActiveStep();
              const isCompleted = step.completed;
              
              return (
                <Step key={step.label} completed={isCompleted}>
                  <StepLabel 
                    StepIconComponent={() => (
                      <CustomStepIcon 
                        active={isActive}
                        completed={isCompleted}
                        icon={step.icon}
                        stepIndex={index}
                      />
                    )}
                    sx={{
                      '& .MuiStepLabel-label': {
                        fontSize: '0.875rem',
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? `${step.color}.main` : 'text.primary'
                      }
                    }}
                  >
                    <Box sx={{ mt: 1 }}>
                      <Typography 
                        variant="body2" 
                        fontWeight={isActive ? 600 : 400}
                        color={isActive ? `${step.color}.main` : 'text.primary'}
                        sx={{ mb: 0.5 }}
                      >
                        {step.label}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color={isActive ? `${step.color}.dark` : 'text.secondary'}
                        sx={{ 
                          display: 'block',
                          fontWeight: isActive ? 500 : 400
                        }}
                      >
                        {step.description}
                      </Typography>
                    </Box>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>

          {/* Progress Bar for Training */}
          {sensor.state === VLM_STATES.INTRAINING && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  Training Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(getProgressPercentage())}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={getProgressPercentage()} 
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Started: {sensor.trainingProgress?.startedAt ? new Date(sensor.trainingProgress.startedAt).toLocaleDateString() : 'Unknown'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ETA: {getTrainingETA()}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Previous Model Info (for retraining) */}
          {isRetraining && sensor.state !== VLM_STATES.CONFIGURED && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloudDoneIcon fontSize="small" />
                Previous Model (Still Active)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Version</Typography>
                  <Typography variant="body2">v{sensor.deploymentInfo?.modelVersion || 'Unknown'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Events Detected</Typography>
                  <Typography variant="body2">{sensor.deploymentInfo?.eventsDetected || 0}</Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Deployed Model Information */}
          {sensor.state === VLM_STATES.DEPLOYED && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip 
                icon={<CloudDoneIcon />}
                label={`Model v${sensor.deploymentInfo?.modelVersion || 'Unknown'}`}
                color="success"
                variant="outlined"
                size="small"
              />
              <Chip 
                label={`${sensor.deploymentInfo?.eventsDetected || 0} Events Detected`}
                variant="outlined"
                size="small"
              />
              {sensor.deploymentInfo?.lastEventAt && (
                <Chip 
                  icon={<AccessTimeIcon />}
                  label={`Last: ${new Date(sensor.deploymentInfo.lastEventAt).toLocaleDateString()}`}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          )}

          {/* Error State */}
          {sensor.state === VLM_STATES.ERROR && sensor.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2">
                {sensor.error.message || sensor.error}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Prompt Configuration Dialog */}
      <Dialog open={showPromptDialog} onClose={() => setShowPromptDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Current Prompt Configurations ({sensor.promptConfigs?.length || 0})
        </DialogTitle>
        <DialogContent>
          {sensor.promptConfigs && sensor.promptConfigs.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {sensor.promptConfigs.map((promptConfig, index) => (
                <Card key={index} variant="outlined">
                  <CardContent>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {promptConfig?.title || `Prompt ${index + 1}`}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Chip label={promptConfig?.category || 'Unknown'} size="small" color="primary" />
                        {promptConfig?.isCustom && <Chip label="Custom" size="small" variant="outlined" />}
                      </Box>
                    </Box>
                    
                    <Typography variant="body2" sx={{ 
                      p: 2, 
                      bgcolor: 'grey.50', 
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'grey.200'
                    }}>
                      {promptConfig?.prompt || 'No prompt configured'}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Alert severity="warning">
              No prompt configurations found.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPromptDialog(false)}>Close</Button>
          <Button variant="contained" onClick={onReconfigure}>
            Reconfigure
          </Button>
        </DialogActions>
      </Dialog>

      {/* Training Progress Dialog */}
      <Dialog open={showProgressDialog} onClose={() => setShowProgressDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Training Progress</DialogTitle>
        <DialogContent>
          <Stepper activeStep={sensor.trainingProgress.currentStep} orientation="vertical">
            {trainingSteps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>
                  <Typography variant="subtitle2">{step.label}</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {sensor.trainingProgress.estimatedCompletion && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Estimated Completion:</strong> {getEstimatedCompletion()}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowProgressDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VLMSensorStateDisplay;
